import { apiClient } from '../api-client';
import { SessionData, setSession, clearSession } from '../session/session-storage';

/**
 * OAuth redirect URL response from GET /v1/oidc/login
 */
export interface OAuthUrlResponse {
  redirect_url: string;
}

/**
 * OAuth login response from POST /v1/oidc/login
 */
export interface OAuthLoginResponse {
  status: string;
}

/**
 * Logout response from DELETE /v1/session
 */
export interface LogoutResponse {
  status: string;
}

/**
 * Authentication service for OAuth2 flow and session management
 * Based on API spec: https://abt-api.avemio.dev/openapi.json
 */
class AuthService {
  /**
   * Get the OAuth authorization URL from the backend
   * GET /v1/oidc/login
   */
  async getAuthorizationUrl(): Promise<string> {
    const response = await apiClient.get<OAuthUrlResponse>('/v1/oidc/login');
    return response.data.redirect_url;
  }

  /**
   * Exchange the authorization code for a session
   * POST /v1/oidc/login with { code, state }
   * Sets session cookie on success
   */
  async exchangeCode(code: string, state: string): Promise<void> {
    await apiClient.post<OAuthLoginResponse>('/v1/oidc/login', {
      code,
      state,
    });

    // Cookie is now set by the API
    // Fetch session data to store locally
    await this.fetchSession();
  }

  /**
   * Get current session info from the server
   * GET /v1/session
   */
  async fetchSession(): Promise<SessionData | null> {
    try {
      const response = await apiClient.get<SessionData>('/v1/session');
      
      // Store session data locally for quick access
      setSession(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch session:', error);
      return null;
    }
  }

  /**
   * Logout and invalidate current session
   * DELETE /v1/session
   */
  async logout(): Promise<void> {
    try {
      await apiClient.delete<LogoutResponse>('/v1/session');
    } catch (error) {
      console.error('Logout request failed:', error);
      // Continue with local cleanup even if server request fails
    } finally {
      clearSession();
    }
  }

  /**
   * Get CSRF token for form protection
   * GET /v1/session/csrf
   */
  async getCsrfToken(): Promise<string> {
    const response = await apiClient.get<{ token: string }>('/v1/session/csrf');
    return response.data.token;
  }
}

export const authService = new AuthService();
